import { useState, useRef, useEffect } from 'react';
import styles from '../styles/EventCard.module.css';
import { trpc } from '../utils/trpc';
import { downloadICS, getGoogleCalendarLink } from '../utils/calendar';
import { EventInfo } from './EventInfo';
import ReactDOM from 'react-dom';
import { useLocation } from 'react-router-dom';
import VenueProfile from '../page-components/VenueProfile';

interface Event {
  event_id: string;
  event_name: string;
  description: string;
  date: string;
  duration_minutes: number;
  comments: number;
  rsvpCount: number;
  rsvpAvatars: string[];
  pinned: boolean;
  venue_id: string;
  status: string;
}

interface EventCardProps {
  event: Event;
  groupId: string;
  animationDelay?: string;
  onEdit?: (event: Event) => void;
  onDelete?: (event: Event) => void;
  onSelect?: (eventId: string) => void;
  onRate?: (eventId: string) => void;
}

/**
 * A template component to render individual event overviews
 */
export function EventCard({
  event,
  groupId,
  animationDelay,
  onEdit,
  onDelete,
  onRate,
}: EventCardProps) {
  const [showCalendarOptions, setShowCalendarOptions] = useState(false);
  const [open, setOpen] = useState(false);
  const [venueProfileOpen, setVenueProfileOpen] = useState(false);

  const location = useLocation();
  const isEventManagementPage = location.pathname.includes(`/groups/${groupId}/events`);
  const wrapperRef = useRef<HTMLDivElement | null>(null); // Ref for click outside

  const { data: venue } = trpc.venue.getById.useQuery({ venue_id: event.venue_id });
  const venueName = venue?.venue_name;
  const venueLocation = venue?.address ?? 'TBD';
  const role = trpc.group.getMyStatus.useQuery({ groupId: groupId ?? '' }).data?.role;
  const canManageEvents = role === 'admin' || role === 'owner' || role === 'moderator';

  // Setup DateTime parsing
  const date = new Date(event.date);
  const eventTime = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const stubDay = date.getDate();
  const stubMonth = date.toLocaleString('default', { month: 'short' }).toUpperCase();

  // Close calendar dropdown if clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowCalendarOptions(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isRsvped =
    trpc.event.getMyRsvp.useQuery({ eventId: event.event_id }).data?.status === 'going';
  const start = new Date(event.date);
  const end = new Date(start.getTime() + (event.duration_minutes ?? 120) * 60000);
  const isPastEvent = end < new Date();
  const alreadyRated =
    trpc.event.getMyRating.useQuery({ eventId: event.event_id }).data?.rating !== null;

  // Standard display
  return (
    <div className={styles.eventCard} style={{ animationDelay }}>
      {/* Date stub (left side) */}
      <div className={styles.cardStub}>
        <span className={styles.stubDay}>{stubDay}</span>
        <span className={styles.stubMonth}>{stubMonth}</span>
        <div className={styles.stubDot} />
      </div>

      {/* Card content */}
      <div className={styles.cardContent}>
        <div className={styles.cardHeader}>
          <h3
            className={styles.eventTitle}
            onClick={() => setOpen(true)}
            style={{ cursor: 'pointer' }}
          >
            {event.event_name}
          </h3>
          {canManageEvents && isEventManagementPage && (
            <div
              className={`${styles.statusChip} ${styles[`status_${event.status.toLowerCase()}`]}`}
              title="Events must be approved by the venue before they become public."
            >
              {event.status}
            </div>
          )}
        </div>

        {/* metadata */}
        <div className={styles.cardMeta}>
          <div className={styles.cardMetaItem}>
            <span className={styles.cardMetaLabel}>Time</span>
            <span className={styles.cardMetaValue}>{eventTime}</span>
          </div>

          <div className={styles.cardMetaItem}>
            <span className={styles.cardMetaLabel}>Where</span>
            <span
              className={styles.cardMetaValue}
              onClick={() => setVenueProfileOpen(true)}
              style={{ cursor: 'pointer', textDecoration: 'underline' }}
            >
              {venueName ?? 'Loading...'}
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className={styles.cardFooter}>
          {canManageEvents && (
            <>
              {!isPastEvent && (
                <button className={styles.actionBtn} onClick={() => onEdit?.(event)}>
                  Edit Event
                </button>
              )}
              <button className={styles.actionBtn} onClick={() => onDelete?.(event)}>
                Delete Event
              </button>
            </>
          )}

          {/* Calendar dropdown */}
          {!isPastEvent && isRsvped && (
            <div ref={wrapperRef} className={styles.calendarWrapper}>
              <button
                className={styles.actionBtn}
                onClick={() => setShowCalendarOptions((s) => !s)}
              >
                📅 Add to Calendar {showCalendarOptions ? '▴' : '▾'}
              </button>

              {showCalendarOptions && (
                <div className={styles.calendarDropdown}>
                  <a
                    href={getGoogleCalendarLink(event, venueLocation)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.dropdownItem}
                    onClick={() => setShowCalendarOptions(false)} // closes dropdown after click
                  >
                    Google Calendar
                  </a>

                  <button
                    onClick={() => {
                      downloadICS(event, venueLocation);
                      setShowCalendarOptions(false); // closes dropdown after click
                    }}
                    className={styles.dropdownItem}
                  >
                    Apple / Outlook
                  </button>
                </div>
              )}
            </div>
          )}
          {isPastEvent && isRsvped && !alreadyRated && (
            <button className={styles.actionBtn} onClick={() => onRate?.(event.event_id)}>
              Rate Event
            </button>
          )}
        </div>
      </div>

      {/* Portals for modals */}
      {open &&
        ReactDOM.createPortal(
          <EventInfo id={event.event_id} onClose={() => setOpen(false)} />,
          document.body,
        )}

      {venueProfileOpen &&
        ReactDOM.createPortal(
          <VenueProfile
            venueId={event.venue_id}
            onClose={() => setVenueProfileOpen(false)}
          />,
          document.body,
        )}
    </div>
  );
}
